import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import Title from "../../../components/titleNew";
import "react-data-table-component-extensions/dist/index.css";
import { arrayBases, converterDataFormatoPadrao } from "../../../util/loja";
import TabelaNext from "../../../components/componentteste/TabelaNext";
import { TituloTabela } from "../../../components/TituloTabelaNew/TituloTabela";
import toast from "react-hot-toast";
import { PermissionsPage } from "../../../util/permissionsPage";
import { crmGestanteService } from "../../../services/crmGestante";
import { useUserData } from "../../../hooks/useUserData";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { LoadingTable } from "../../../components/Loading/LoadingTable";
import { MultiSelectFilterBases } from "../../../components/FilterLojaMetas/MultiSelectFilterBases";
import { FiltroPeriodo } from "../../../components/FiltroPeriodo/FiltroPeriodo";
import { ButtonFilter } from "../../../components/buttonFilter";
import { colunasVendasCrm } from "./table";

const calculateWidth = (data) => {
  if (!data || !data.labels) return 800;
  return Math.max(800, data.labels.length * 140);
};

const chartOptions = {
  maintainAspectRatio: false,
  responsive: false,
  aspectRatio: 1,
  title: {
    display: true,
    padding: 0,
    text: "Vendas e Clientes via CRM Gestante",
    fontColor: "#FC68A2",
    fontSize: 20,
    align: "left",
  },
  legend: { position: "bottom", align: "start" },
  layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
  scales: {
    yAxes: [
      {
        id: "y-axis-1",
        position: "left",
        ticks: { beginAtZero: true },
      },
      {
        id: "y-axis-2",
        position: "right",
        gridLines: { drawOnChartArea: false },
        ticks: { beginAtZero: true },
      },
    ],
    xAxes: [{ ticks: { padding: 25 } }],
  },
  tooltips: {
    mode: "index",
    intersect: false,
    callbacks: {
      label: function (tooltipItem, data) {
        let label = data.datasets[tooltipItem.datasetIndex].label || "";
        if (label) label += ": ";
        if (data.datasets[tooltipItem.datasetIndex].yAxisID === "y-axis-1") {
          label += parseFloat(tooltipItem.yLabel || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });
        } else {
          label += parseFloat(tooltipItem.yLabel || 0).toLocaleString("pt-BR");
        }
        return label;
      },
    },
  },
  plugins: {
    datalabels: {
      labels: { title: { font: { size: 11 } } },
    },
  },
};

function buildChart(vendasPorBase) {
  if (!vendasPorBase || vendasPorBase.length === 0) return null;
  const labels = vendasPorBase.map((v) => `Base ${v.codBase}`);
  return {
    labels,
    datasets: [
      {
        label: "Vendas CRM (R$)",
        data: vendasPorBase.map((v) => v.vlrTotal),
        backgroundColor: "rgba(91, 139, 252, 1)",
        yAxisID: "y-axis-1",
        datalabels: {
          anchor: "end",
          display: true,
          color: "#000",
          align: "end",
          formatter: (v) =>
            parseFloat(v).toLocaleString("pt-BR", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
        },
      },
      {
        label: "Qtd. Clientes",
        data: vendasPorBase.map((v) => v.totalClientes),
        backgroundColor: "#FFDB8A",
        yAxisID: "y-axis-2",
        datalabels: {
          anchor: "end",
          display: true,
          color: "#000",
          align: "end",
          formatter: (v) =>
            parseFloat(v).toLocaleString("pt-BR", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
        },
      },
    ],
  };
}

function buildTableRows(vendas) {
  if (!vendas || vendas.length === 0) return [];

  const totalPedidos = vendas.reduce((s, v) => s + v.totalPedidos, 0);
  const vlrTotal = vendas.reduce((s, v) => s + v.vlrTotal, 0);
  const vlrCusto = vendas.reduce((s, v) => s + v.vlrCusto, 0);
  const vlrLucroBruto = vendas.reduce((s, v) => s + v.vlrLucroBruto, 0);
  const totalClientes = vendas.reduce((s, v) => s + v.totalClientes, 0);

  const rows = vendas.map((v) => ({
    base: `Base ${v.codBase}`,
    totalPedidos: v.totalPedidos,
    vlrTotal: v.vlrTotal,
    totalClientes: v.totalClientes,
    ticketMedio: v.ticketMedio,
    margemPct: v.margemPct,
    representatividadePct: totalPedidos > 0 ? (v.totalPedidos / totalPedidos) * 100 : 0,
  }));

  rows.push({
    base: "Resumo",
    totalPedidos,
    vlrTotal,
    totalClientes,
    ticketMedio: totalPedidos > 0 ? vlrTotal / totalPedidos : 0,
    margemPct: vlrTotal > 0 ? (vlrLucroBruto / vlrTotal) * 100 : 0,
    representatividadePct: 100,
  });

  return rows;
}

function CrmGestanteVendas() {
  const { userData } = useUserData();
  const accessLevel = userData?.nivelAcesso;
  const acessBase = userData?.baseAcesso;

  const [isLoading, setIsLoading] = useState(true);
  const [periodoInputInicio, setPeriodoInputInicio] = useState("");
  const [periodoInputFim, setPeriodoInputFim] = useState("");
  const [base, setBase] = useState(null);
  const [basesDisponiveis, setBasesDisponiveis] = useState([]);
  const [basesSelecionadas, setBasesSelecionadas] = useState([]);
  const [nivelAcesso, setNivelAcesso] = useState([]);
  const [searchParams, setSearchParams] = useState(null);

  const [dadosGrafico, setDadosGrafico] = useState(null);
  const [dadosParaTabela, setDadosParaTabela] = useState([]);
  const [dadosDisponiveis, setDadosDisponiveis] = useState(true);

  const uniqueKey = useMemo(() => Date.now(), [dadosGrafico]);

  useEffect(() => {
    if (nivelAcesso.length === 0) {
      buscarNivelAcesso();
      setIsLoading(true);
    }
  }, [userData]);

  useMemo(() => {
    preencherDados();
  }, [searchParams]);

  useEffect(() => {
    preencherInputPeriodo();
  }, []);

  const buscarNivelAcesso = () => {
    if (accessLevel) {
      setNivelAcesso(accessLevel);
      const bases = arrayBases(acessBase);
      setBasesDisponiveis(bases);
      if (bases?.length > 0) {
        const baseTemp = bases[0]?.value || bases[0]?.codBase;
        setBase([baseTemp]);
        setBasesSelecionadas([baseTemp]);
      } else {
        setBase(null);
        setBasesSelecionadas([]);
      }
    } else {
      setIsLoading(false);
    }
  };

  function preencherInputPeriodo() {
    const dataHoje = new Date();
    const [inicioMes, hoje] = converterDataFormatoPadrao(dataHoje);
    setPeriodoInputInicio(inicioMes);
    setPeriodoInputFim(hoje);
    iniciarFiltro({ startDate: inicioMes, finalDate: hoje, bases: [] });
  }

  function iniciarFiltro(defaultValue = null) {
    if (defaultValue) return setSearchParams(defaultValue);
    setSearchParams({
      startDate: periodoInputInicio,
      finalDate: periodoInputFim,
      bases: basesSelecionadas,
    });
  }

  async function preencherDados() {
    if (!searchParams) return;
    setIsLoading(true);
    window.scrollTo(0, 0);
    const toastId = toast.loading("Carregando relatório...");
    try {
      const response = await crmGestanteService.relatorioVendas({
        startDate: searchParams.startDate,
        finalDate: searchParams.finalDate,
        bases: searchParams.bases || [],
      });

      if (!response?.data) {
        toast.error("Não foi possível carregar os dados", {
          duration: 7000,
          style: { borderRadius: "0.25rem", background: "#333", color: "#fff" },
        });
        setIsLoading(false);
        setDadosDisponiveis(false);
        return;
      }

      if (response.status === 200) {
        toast.success("Dados recebidos", { id: toastId });
        const vendas = response.data.vendas || [];

        if (vendas.length === 0) {
          setDadosDisponiveis(false);
          setIsLoading(false);
          return;
        }

        setDadosGrafico(buildChart(vendas));
        setDadosParaTabela(buildTableRows(vendas));
        setDadosDisponiveis(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      toast.dismiss(toastId);
      setIsLoading(false);
    }
  }

  const filtrarBases = (value) => {
    setBasesSelecionadas(value);
    setBase(value);
  };

  return (
    // PermissionsPage("crmGestanteVendas") && (
      <>
        <Box ml={2}>
          <Title title="CRM Gestante — Vendas Geradas" />
        </Box>

        <Stack gap={2} direction={"row"} mx={2}>
          <Box component={Paper} variant="outlined" px={1} pb={1}>
            <FiltroPeriodo
              periodoInputInicioAux={periodoInputInicio}
              periodoInputFimAux={periodoInputFim}
              setPeriodoInputInicioAux={setPeriodoInputInicio}
              setPeriodoInputFimAux={setPeriodoInputFim}
              esconderAte={true}
              titulo="Período da venda"
            />
          </Box>
          <Box width={"100%"}>
            <Box pb={1}>
              <MultiSelectFilterBases
                basesDisponiveis={basesDisponiveis}
                basesSelecionadas={basesSelecionadas}
                onChange={filtrarBases}
                disabled={isLoading}
              />
              <Typography variant="caption" component={"span"}>
                *Quando nenhuma base estiver selecionada, todas as bases serão
                retornadas.*
              </Typography>
            </Box>
            <Box>
              <ButtonFilter
                type="button"
                onClick={() => iniciarFiltro()}
                disabled={isLoading}
              >
                Filtrar
              </ButtonFilter>
            </Box>
          </Box>
        </Stack>

        {isLoading ? (
          <Box
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"90vh"}
          >
            <LoadingTable isLoading={isLoading} />
          </Box>
        ) : (
          <>
            {!dadosDisponiveis && (
              <Box mx={2} mt={3} textAlign="center">
                <Typography color="text.secondary">
                  Nenhuma venda originada pelo CRM Gestante no período selecionado.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  As vendas são rastreadas quando a vendedora clica em "Iniciar
                  Venda" no CRM Gestante do PDV.
                </Typography>
              </Box>
            )}

            {dadosDisponiveis && dadosGrafico && (
              <Box mx={2} mt={2} component={Paper} variant="outlined" p={2}>
                <Box sx={{ overflowX: "scroll" }}>
                  <Bar
                    key={"grafico_vendas_crm_" + uniqueKey}
                    height={460}
                    width={calculateWidth(dadosGrafico)}
                    data={dadosGrafico}
                    options={chartOptions}
                  />
                </Box>
              </Box>
            )}

            {dadosDisponiveis && dadosParaTabela.length > 0 && (
              <Box m={2} variant="outlined" style={{ overflow: "auto" }}>
                <TituloTabela>Vendas por Base</TituloTabela>
                <TabelaNext
                  dadosParaTabela={dadosParaTabela}
                  colunas={colunasVendasCrm}
                />
              </Box>
            )}
          </>
        )}
      </>
    // )
  );
}

export default CrmGestanteVendas;
